import { createGlobalStyle } from 'styled-components';
import { palette, derived, semantic, semanticSurface, dataviz } from './tokens';

/**
 * Tokens semânticos — Camada 2. Interface pública do design system.
 *
 * Componentes consomem exclusivamente estes tokens via `var(--nome)`.
 * Nomes descrevem PAPEL, não aparência: `--action-accent-surface`, nunca `--ciano`.
 *
 * Restrição crítica: `palette.teal` e `palette.mint` reprovam WCAG AA como texto sobre
 * fundo claro (1.99:1 e 1.50:1). Só existem como superfície ou sobre fundo escuro.
 * Para texto de ação use `--action-link`. Ver contracts/tokens.contract.md (C3).
 */
export const GlobalStyle = createGlobalStyle`

    :root {
        /* Superfícies — os 60% neutros da hierarquia do Brand Book */
        --surface-canvas: ${palette.offWhite};
        --surface-raised: ${palette.white};
        --surface-sunken: ${palette.stone};
        --surface-inverse: ${palette.graphite};

        /* Texto */
        --text-primary: ${palette.graphite};
        --text-secondary: ${palette.slate};
        --text-disabled: ${derived.slateLight};
        --text-on-inverse: ${palette.offWhite};
        --text-on-accent: ${palette.graphite};

        /* Ação — 30% institucional + 10% expressão */
        --action-primary-surface: ${palette.graphite};
        --action-primary-text: ${palette.offWhite};
        --action-accent-surface: ${palette.teal};
        --action-accent-text: ${palette.graphite};
        --action-link: ${derived.tealDeep};
        --action-disabled-surface: ${palette.stone};
        --action-disabled-text: ${derived.slateLight};

        /* Semânticos */
        --success: ${semantic.success};
        --success-text: ${semantic.success};
        --error: ${semantic.error};
        --error-text: ${semantic.error};
        --warning: ${semantic.warning};
        --warning-text: ${semantic.warning};
        --info: ${semantic.info};
        --info-text: ${semantic.info};

        /* Superfícies semânticas — fundos de alerta/banner/chip */
        --success-surface: ${semanticSurface.success};
        --error-surface: ${semanticSurface.error};
        --warning-surface: ${semanticSurface.warning};
        --info-surface: ${semanticSurface.info};

        /* Bordas e linhas — a "malha invisível" do Cap. 08 */
        --border-hairline: ${palette.slate};
        --border-accent: ${derived.tealMid};
        --border-default: ${palette.stone};
        --focus-ring: ${derived.tealDeep};

        /* Visualização de dados — sequência ordenada, alterna luminâncias */
        --dv-1: ${dataviz[0]};
        --dv-2: ${dataviz[1]};
        --dv-3: ${dataviz[2]};
        --dv-4: ${dataviz[3]};
        --dv-5: ${dataviz[4]};
        --dv-6: ${dataviz[5]};
        --dv-7: ${dataviz[6]};
        --dv-8: ${dataviz[7]};

        /* Tipografia — famílias */
        --font-display: 'Source Serif 4', Georgia, serif;
        --font-body: 'Inter', system-ui, -apple-system, sans-serif;
        --font-mono: 'IBM Plex Mono', ui-monospace, monospace;

        /* Tipografia — escala (rem herda a redução responsiva de html abaixo) */
        --type-display: 4rem;
        --type-h1: 2.5rem;
        --type-h2: 1.75rem;
        --type-body: 1rem;
        --type-caption: 0.75rem;
        --type-data: 1rem;

        --leading-display: 1.1;
        --leading-heading: 1.2;
        --leading-body: 1.55;
        --leading-caption: 1.4;

        --tracking-display: -0.03em;
        --tracking-heading: -0.02em;
        --tracking-caption: 0.08em;

        /* Forma */
        --radius-sm: 4px;
        --radius-md: 8px;
        --radius-lg: 16px;
        --hairline: 1px;

        /* Espaçamento */
        --space-1: 4px;
        --space-2: 8px;
        --space-3: 16px;
        --space-4: 24px;
        --space-5: 32px;
        --space-6: 48px;
        --space-7: 64px;

        /* Elevação */
        --elevation-1: 0 1px 2px rgba(32, 41, 48, 0.06);
        --elevation-2: 0 2px 8px rgba(32, 41, 48, 0.08);
        --elevation-3: 0 8px 24px rgba(32, 41, 48, 0.12);
        --elevation-accent: 0 6px 20px rgba(41, 199, 184, 0.35);
        --overlay-scrim: rgba(32, 41, 48, 0.32);

        /*
         * Gradiente do hero. Definido aqui como token porque o valor traz cor com
         * alpha, que não se expressa por var() dentro de linear-gradient sem
         * duplicar literais em cada consumidor.
         */
        --gradient-hero: linear-gradient(
            180.78deg,
            rgba(41, 199, 184, 0.8) -25.93%,
            rgba(68, 94, 115, 0.2) 67.26%,
            rgba(68, 94, 115, 0) 92.62%
        );
    }

    * {
        margin: 0;
        padding: 0;
        box-sizing: border-box ;
    }

    html {
        @media (max-width: 1080px) {
            font-size: 93.75% ;
        }

        @media (max-width: 720px) {
            font-size: 87.5%;
        }
    }

    body {
       background: var(--surface-canvas);
       color: var(--text-primary);
        -webkit-font-smoothing: antialiased;
    }

    body, input, textarea, button {
        font-family: var(--font-body);
        font-weight: 400;
        line-height: var(--leading-body);
    }

    h1, h2, h3, h4, h5, h6, strong {
        font-family: var(--font-display);
        font-weight: 500;
        letter-spacing: var(--tracking-heading);
        line-height: var(--leading-heading);
    }

    button {
        cursor: pointer;
    }

    /*
     * Dados, identificadores e códigos de sistema (CNPJ, protocolos, valores).
     * Brand Book Cap. 06: mono para dados. As tabulares alinham os dígitos em
     * colunas, o que facilita a comparação vertical de valores.
     */
    .data-mono {
        font-family: var(--font-mono);
        font-variant-numeric: tabular-nums;
    }

    /* Legendas — caixa alta com tracking aberto (Brand Book Cap. 06) */
    .caption {
        font-size: var(--type-caption);
        line-height: var(--leading-caption);
        letter-spacing: var(--tracking-caption);
        text-transform: uppercase;
        color: var(--text-secondary);
    }

    [disabled] {
        opacity: 0.6;
        cursor: not-allowed ;
    }

    :focus-visible {
        outline: 2px solid var(--focus-ring);
        outline-offset: 2px;
    }

`;
